import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Slider,
  Button,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Paper,
  Tooltip,
  IconButton
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import InfoIcon from '@mui/icons-material/Info';

import {
  getQualityGatesConfig,
  updateQualityGatesConfig,
  getComplianceConfig,
  updateComplianceConfig,
  getProximityConfig,
  updateProximityConfig,
  getVisibleCharacteristics,
  updateVisibleCharacteristics,
  getFaceMatchingConfig,
  updateFaceMatchingConfig,
  getSOIPromotionConfig,
  updateSOIPromotionConfig,
  getMatchThresholdsConfig,
  updateMatchThresholdsConfig
} from '../services/api/tenantConfigService';
import characteristicsList from '../components/static/characteristics.json';

// System defaults
const DEFAULT_QUALITY_CONFIG = {
  min_face_confidence: 0,
  min_face_width: 0,
  min_face_height: 0,
  min_sharpness: 0,
  min_brightness: 0,
  max_brightness: 100
};

const DEFAULT_RECURRING_CONFIG = {
  min_time_gap: 60
};

const DEFAULT_COMPLIANCE_CONFIG = {
  ignore_unrelated_faces: false,
  matched_faces_only: false,
  create_soi_events: false
};

const DEFAULT_PROXIMITY_CONFIG = {
  indoor_high_proximity_count: 1,
  indoor_medium_proximity_count: 2,
  indoor_high_proximity_distance: 1.0,
  indoor_medium_proximity_distance: 2.5,
  indoor_low_proximity_distance: 4.0
};

const DEFAULT_FACE_MATCHING_CONFIG = {
  known_threshold: 80,
  associated_threshold: 85,
  known_max_faces: 1,
  associated_max_faces: 1
};

const DEFAULT_SOI_PROMOTION_CONFIG = {
  enabled: false,
  min_sighting_count: 5,
  min_unique_days: 2
};

const DEFAULT_MATCH_THRESHOLDS_CONFIG = {
  known: 85,
  soi: 80,
  associated: 75
};

// Default: all characteristics visible
const DEFAULT_VISIBLE_CHARACTERISTICS = characteristicsList.reduce((acc, char) => {
  acc[char] = true;
  return acc;
}, {});

export default function TenantConfigAdmin() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [config, setConfig] = useState({ ...DEFAULT_QUALITY_CONFIG, ...DEFAULT_RECURRING_CONFIG });
  const [originalConfig, setOriginalConfig] = useState(null);
  const [complianceConfig, setComplianceConfig] = useState({ ...DEFAULT_COMPLIANCE_CONFIG });
  const [originalComplianceConfig, setOriginalComplianceConfig] = useState(null);
  const [proximityConfig, setProximityConfig] = useState({ ...DEFAULT_PROXIMITY_CONFIG });
  const [originalProximityConfig, setOriginalProximityConfig] = useState(null);
  const [faceMatchingConfig, setFaceMatchingConfig] = useState({ ...DEFAULT_FACE_MATCHING_CONFIG });
  const [originalFaceMatchingConfig, setOriginalFaceMatchingConfig] = useState(null);
  const [soiPromotionConfig, setSOIPromotionConfig] = useState({ ...DEFAULT_SOI_PROMOTION_CONFIG });
  const [originalSOIPromotionConfig, setOriginalSOIPromotionConfig] = useState(null);
  const [matchThresholdsConfig, setMatchThresholdsConfig] = useState({ ...DEFAULT_MATCH_THRESHOLDS_CONFIG });
  const [originalMatchThresholdsConfig, setOriginalMatchThresholdsConfig] = useState(null);
  const [visibleCharacteristics, setVisibleCharacteristics] = useState({ ...DEFAULT_VISIBLE_CHARACTERISTICS });
  const [originalVisibleCharacteristics, setOriginalVisibleCharacteristics] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const [qualityConfig, compliance, proximity, faceMatching, soiPromotion, matchThresholds, visibleChars] = await Promise.all([
        getQualityGatesConfig(),
        getComplianceConfig(),
        getProximityConfig(),
        getFaceMatchingConfig(),
        getSOIPromotionConfig(),
        getMatchThresholdsConfig(),
        getVisibleCharacteristics()
      ]);
      setConfig(qualityConfig);
      setOriginalConfig(qualityConfig);
      setComplianceConfig(compliance);
      setOriginalComplianceConfig(compliance);
      setProximityConfig(proximity);
      setOriginalProximityConfig(proximity);
      setFaceMatchingConfig(faceMatching);
      setOriginalFaceMatchingConfig(faceMatching);
      setSOIPromotionConfig(soiPromotion);
      setOriginalSOIPromotionConfig(soiPromotion);
      setMatchThresholdsConfig(matchThresholds);
      setOriginalMatchThresholdsConfig(matchThresholds);
      // Merge with defaults to ensure all characteristics have a value
      const mergedVisibleChars = { ...DEFAULT_VISIBLE_CHARACTERISTICS, ...visibleChars };
      setVisibleCharacteristics(mergedVisibleChars);
      setOriginalVisibleCharacteristics(mergedVisibleChars);
    } catch (err) {
      setError('Failed to load configuration: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const [updatedConfig, updatedCompliance, updatedProximity, updatedFaceMatching, updatedSOIPromotion, updatedMatchThresholds, updatedVisibleChars] = await Promise.all([
        updateQualityGatesConfig(config),
        updateComplianceConfig(complianceConfig),
        updateProximityConfig(proximityConfig),
        updateFaceMatchingConfig(faceMatchingConfig),
        updateSOIPromotionConfig(soiPromotionConfig),
        updateMatchThresholdsConfig(matchThresholdsConfig),
        updateVisibleCharacteristics(visibleCharacteristics)
      ]);
      setConfig(updatedConfig);
      setOriginalConfig(updatedConfig);
      setComplianceConfig(updatedCompliance);
      setOriginalComplianceConfig(updatedCompliance);
      setProximityConfig(updatedProximity);
      setOriginalProximityConfig(updatedProximity);
      setFaceMatchingConfig(updatedFaceMatching);
      setOriginalFaceMatchingConfig(updatedFaceMatching);
      setSOIPromotionConfig(updatedSOIPromotion);
      setOriginalSOIPromotionConfig(updatedSOIPromotion);
      setMatchThresholdsConfig(updatedMatchThresholds);
      setOriginalMatchThresholdsConfig(updatedMatchThresholds);
      setVisibleCharacteristics(updatedVisibleChars);
      setOriginalVisibleCharacteristics(updatedVisibleChars);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save configuration: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (originalConfig) setConfig(originalConfig);
    if (originalComplianceConfig) setComplianceConfig(originalComplianceConfig);
    if (originalProximityConfig) setProximityConfig(originalProximityConfig);
    if (originalFaceMatchingConfig) setFaceMatchingConfig(originalFaceMatchingConfig);
    if (originalSOIPromotionConfig) setSOIPromotionConfig(originalSOIPromotionConfig);
    if (originalVisibleCharacteristics) setVisibleCharacteristics(originalVisibleCharacteristics);
    setSuccess(false);
    setError(null);
  };

  const hasChanges =
    (originalConfig && JSON.stringify(config) !== JSON.stringify(originalConfig)) ||
    (originalComplianceConfig && JSON.stringify(complianceConfig) !== JSON.stringify(originalComplianceConfig)) ||
    (originalProximityConfig && JSON.stringify(proximityConfig) !== JSON.stringify(originalProximityConfig)) ||
    (originalFaceMatchingConfig && JSON.stringify(faceMatchingConfig) !== JSON.stringify(originalFaceMatchingConfig)) ||
    (originalSOIPromotionConfig && JSON.stringify(soiPromotionConfig) !== JSON.stringify(originalSOIPromotionConfig)) ||
    (originalVisibleCharacteristics && JSON.stringify(visibleCharacteristics) !== JSON.stringify(originalVisibleCharacteristics));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 1400 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Tenant Configuration</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={handleReset}
            disabled={!hasChanges || saving}
            sx={{ mr: 1 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Configuration saved successfully!
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Quality Gates */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1.1rem' }}>
                Quality Gates
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Face Confidence</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{config.min_face_confidence}%</Typography>
                  <Slider
                    value={config.min_face_confidence}
                    onChange={(e, val) => setConfig({...config, min_face_confidence: val})}
                    min={0}
                    max={100}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Face Width</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{(config.min_face_width * 100).toFixed(0)}%</Typography>
                  <Slider
                    value={config.min_face_width}
                    onChange={(e, val) => setConfig({...config, min_face_width: val})}
                    min={0}
                    max={0.5}
                    step={0.01}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Face Height</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{(config.min_face_height * 100).toFixed(0)}%</Typography>
                  <Slider
                    value={config.min_face_height}
                    onChange={(e, val) => setConfig({...config, min_face_height: val})}
                    min={0}
                    max={0.5}
                    step={0.01}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Sharpness</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{config.min_sharpness}</Typography>
                  <Slider
                    value={config.min_sharpness}
                    onChange={(e, val) => setConfig({...config, min_sharpness: val})}
                    min={0}
                    max={100}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Min Brightness</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{config.min_brightness}</Typography>
                  <Slider
                    value={config.min_brightness}
                    onChange={(e, val) => setConfig({...config, min_brightness: val})}
                    min={0}
                    max={100}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Max Brightness</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{config.max_brightness}</Typography>
                  <Slider
                    value={config.max_brightness}
                    onChange={(e, val) => setConfig({...config, max_brightness: val})}
                    min={0}
                    max={100}
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Face Matching Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                  Face Matching
                </Typography>
                <Tooltip title="Rekognition search parameters for face matching">
                  <IconButton size="small" sx={{ ml: 0.5 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Known Threshold (%)</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{faceMatchingConfig.known_threshold}%</Typography>
                  <Slider
                    value={faceMatchingConfig.known_threshold}
                    onChange={(e, val) => setFaceMatchingConfig({...faceMatchingConfig, known_threshold: val})}
                    min={0}
                    max={100}
                    step={1}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Associated Threshold (%)</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{faceMatchingConfig.associated_threshold}%</Typography>
                  <Slider
                    value={faceMatchingConfig.associated_threshold}
                    onChange={(e, val) => setFaceMatchingConfig({...faceMatchingConfig, associated_threshold: val})}
                    min={0}
                    max={100}
                    step={1}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Known Max Faces</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{faceMatchingConfig.known_max_faces}</Typography>
                  <Slider
                    value={faceMatchingConfig.known_max_faces}
                    onChange={(e, val) => setFaceMatchingConfig({...faceMatchingConfig, known_max_faces: val})}
                    min={1}
                    max={10}
                    step={1}
                    marks
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Max matches from known collection
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Associated Max Faces</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{faceMatchingConfig.associated_max_faces}</Typography>
                  <Slider
                    value={faceMatchingConfig.associated_max_faces}
                    onChange={(e, val) => setFaceMatchingConfig({...faceMatchingConfig, associated_max_faces: val})}
                    min={1}
                    max={10}
                    step={1}
                    marks
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Max matches from associated collection
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Proximity Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                  Proximity Analysis
                </Typography>
                <Tooltip title="Distance thresholds for classifying proximity between faces (as multiples of average face size)">
                  <IconButton size="small" sx={{ ml: 0.5 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Grid container spacing={1.5}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">HIGH Distance</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{proximityConfig.indoor_high_proximity_distance?.toFixed(1)}x</Typography>
                  <Slider
                    value={proximityConfig.indoor_high_proximity_distance}
                    onChange={(e, val) => setProximityConfig({...proximityConfig, indoor_high_proximity_distance: val})}
                    min={0.5}
                    max={2.0}
                    step={0.1}
                    size="small"
                  />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">MEDIUM Distance</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{proximityConfig.indoor_medium_proximity_distance?.toFixed(1)}x</Typography>
                  <Slider
                    value={proximityConfig.indoor_medium_proximity_distance}
                    onChange={(e, val) => setProximityConfig({...proximityConfig, indoor_medium_proximity_distance: val})}
                    min={1.0}
                    max={4.0}
                    step={0.1}
                    size="small"
                  />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">LOW Distance</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{proximityConfig.indoor_low_proximity_distance?.toFixed(1)}x</Typography>
                  <Slider
                    value={proximityConfig.indoor_low_proximity_distance}
                    onChange={(e, val) => setProximityConfig({...proximityConfig, indoor_low_proximity_distance: val})}
                    min={2.0}
                    max={8.0}
                    step={0.5}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mt: 1, mb: 0.5 }}>
                    Associated Person Promotion Thresholds (Sightings)
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">HIGH Proximity</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{proximityConfig.indoor_high_proximity_count}</Typography>
                  <Slider
                    value={proximityConfig.indoor_high_proximity_count}
                    onChange={(e, val) => setProximityConfig({...proximityConfig, indoor_high_proximity_count: val})}
                    min={1}
                    max={5}
                    marks
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">MEDIUM Proximity</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{proximityConfig.indoor_medium_proximity_count}</Typography>
                  <Slider
                    value={proximityConfig.indoor_medium_proximity_count}
                    onChange={(e, val) => setProximityConfig({...proximityConfig, indoor_medium_proximity_count: val})}
                    min={1}
                    max={10}
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Promotion Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1.1rem' }}>
                Ignore Duplicate Matches within Time Window
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Minimum Time Gap (minutes)</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{config.min_time_gap}</Typography>
                  <Slider
                    value={config.min_time_gap}
                    onChange={(e, val) => setConfig({...config, min_time_gap: val})}
                    min={0}
                    max={1440}
                    step={15}
                    marks={[
                      { value: 0, label: '0m' },
                      { value: 60, label: '1h' },
                      { value: 360, label: '6h' },
                      { value: 720, label: '12h' },
                      { value: 1440, label: '24h' }
                    ]}
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Suppress duplicate match events within this time window
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Compliance Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1.1rem' }}>
                Compliance & Privacy
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={complianceConfig.ignore_unrelated_faces}
                        onChange={(e) => setComplianceConfig({...complianceConfig, ignore_unrelated_faces: e.target.checked})}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Ignore Unrelated Faces</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Do not store or process faces unrelated to known persons
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={complianceConfig.matched_faces_only}
                        onChange={(e) => setComplianceConfig({...complianceConfig, matched_faces_only: e.target.checked})}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Matched Faces Only</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Store only matched faces (higher privacy, lower debug capability)
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={complianceConfig.create_soi_events}
                        onChange={(e) => setComplianceConfig({...complianceConfig, create_soi_events: e.target.checked})}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Enable SOI (Subject of Interest) Promotion</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Allow operators to flag unknown faces as Subjects of Interest (manual or automatic promotion)
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* SOI Promotion Settings - Only visible if create_soi_events is enabled */}
        {complianceConfig.create_soi_events && (
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1.1rem' }}>
                  SOI Auto-Promotion
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={soiPromotionConfig.enabled}
                          onChange={(e) => setSOIPromotionConfig({...soiPromotionConfig, enabled: e.target.checked})}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">Enable Automatic Promotion</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Automatically promote unknown faces to SOI when criteria met
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography gutterBottom>
                      Minimum Sighting Count: {soiPromotionConfig.min_sighting_count}
                    </Typography>
                    <Slider
                      value={soiPromotionConfig.min_sighting_count}
                      onChange={(e, val) => setSOIPromotionConfig({...soiPromotionConfig, min_sighting_count: val})}
                      min={1}
                      max={20}
                      step={1}
                      marks={[
                        { value: 1, label: '1' },
                        { value: 5, label: '5' },
                        { value: 10, label: '10' },
                        { value: 20, label: '20' }
                      ]}
                      disabled={!soiPromotionConfig.enabled}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography gutterBottom>
                      Minimum Unique Days: {soiPromotionConfig.min_unique_days}
                    </Typography>
                    <Slider
                      value={soiPromotionConfig.min_unique_days}
                      onChange={(e, val) => setSOIPromotionConfig({...soiPromotionConfig, min_unique_days: val})}
                      min={1}
                      max={10}
                      step={1}
                      marks={[
                        { value: 1, label: '1' },
                        { value: 2, label: '2' },
                        { value: 5, label: '5' },
                        { value: 10, label: '10' }
                      ]}
                      disabled={!soiPromotionConfig.enabled}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Match Thresholds */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1.1rem' }}>
                Face Match Similarity Thresholds
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Configure minimum similarity scores for matching faces in each tier. Higher values = stricter matching (fewer false positives).
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    Known Persons: {matchThresholdsConfig.known}%
                  </Typography>
                  <Slider
                    value={matchThresholdsConfig.known}
                    onChange={(e, val) => setMatchThresholdsConfig({...matchThresholdsConfig, known: val})}
                    min={70}
                    max={95}
                    step={1}
                    marks={[
                      { value: 70, label: '70%' },
                      { value: 85, label: '85%' },
                      { value: 95, label: '95%' }
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    SOI (Subject of Interest): {matchThresholdsConfig.soi}%
                  </Typography>
                  <Slider
                    value={matchThresholdsConfig.soi}
                    onChange={(e, val) => setMatchThresholdsConfig({...matchThresholdsConfig, soi: val})}
                    min={70}
                    max={95}
                    step={1}
                    marks={[
                      { value: 70, label: '70%' },
                      { value: 80, label: '80%' },
                      { value: 95, label: '95%' }
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>
                    Associated Persons: {matchThresholdsConfig.associated}%
                  </Typography>
                  <Slider
                    value={matchThresholdsConfig.associated}
                    onChange={(e, val) => setMatchThresholdsConfig({...matchThresholdsConfig, associated: val})}
                    min={65}
                    max={90}
                    step={1}
                    marks={[
                      { value: 65, label: '65%' },
                      { value: 75, label: '75%' },
                      { value: 90, label: '90%' }
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Characteristic Visibility */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                  Characteristic Visibility
                </Typography>
                <Tooltip title="Control which facial characteristics appear in the search selector">
                  <IconButton size="small" sx={{ ml: 0.5 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Toggle which characteristics are available for selection in search filters
              </Typography>
              <Grid container spacing={1}>
                {characteristicsList.map(char => (
                  <Grid item xs={6} sm={4} md={3} key={char}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={visibleCharacteristics[char] !== false}
                          onChange={(e) => setVisibleCharacteristics({
                            ...visibleCharacteristics,
                            [char]: e.target.checked
                          })}
                        />
                      }
                      label={<Typography variant="body2">{char}</Typography>}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
